/**
 * The threads I've opened, kept on the device (SnapshotStore, one file per thread and a small index, per workspace and
 * user) so the reader shows them at once and then refreshes. Bounded by `trimMailThreadCache`; the files go with the other
 * snapshots at sign-out. Nothing here logs: they hold mail.
 */
import type { MailBodyView, MailMessage, MailThread } from "@bundle/contracts";
import { MAIL_THREAD_CACHE_BYTES, MAIL_THREAD_CACHE_MAX, MAIL_THREAD_ENTRY_BYTES, trimMailThreadCache, type MailThreadCacheEntry } from "../logic/mail.ts";
import type { SnapshotStore } from "../ports.ts";

export const MAIL_THREAD_SNAPSHOT_VERSION = 1;
/** What a message costs besides its text and body: its headers, addresses and attachments. */
const MESSAGE_BYTES = 2_000;

export const mailThreadIndexKey = (wsId: string, userId: string) => `mail-threads.${wsId}.${userId}`;
export const mailThreadKey = (wsId: string, userId: string, threadId: string) => `mail-thread.${wsId}.${userId}.${threadId}`;

export type MailCachedBody = { html: string; view: MailBodyView; canRead: boolean };

/** A thread as I last read it. Bodies only as the server sends them with images blocked. */
export type MailThreadSnapshot = {
  v: number;
  thread: MailThread;
  messages: MailMessage[];
  expanded: string[];
  bodies: Record<string, MailCachedBody>;
};

/** The snapshot in `raw`, or undefined when it's unreadable or an older format. */
export function parseMailThreadSnapshot(raw: string): MailThreadSnapshot | undefined {
  try {
    const v = JSON.parse(raw) as Partial<MailThreadSnapshot> | null;
    return v?.v === MAIL_THREAD_SNAPSHOT_VERSION && v.thread !== undefined && Array.isArray(v.messages) && Array.isArray(v.expanded) && typeof v.bodies === "object" ? (v as MailThreadSnapshot) : undefined;
  } catch {
    return undefined;
  }
}

/** The size that counts against the cache. */
const threadBytes = (messages: MailMessage[], bodies: Record<string, MailCachedBody>) => messages.reduce((n, m) => n + MESSAGE_BYTES + m.text.length + (bodies[m.id]?.html.length ?? 0), 0);

/** Fits a thread into its share of the cache, keeping the newest messages' bodies. */
export function fitMailThreadBodies(messages: MailMessage[], bodies: Record<string, MailCachedBody>): Record<string, MailCachedBody> {
  const kept: Record<string, MailCachedBody> = {};
  let bytes = threadBytes(messages, {});
  for (const m of [...messages].reverse()) {
    const body = bodies[m.id];
    if (body === undefined || bytes + body.html.length > MAIL_THREAD_ENTRY_BYTES) continue;
    kept[m.id] = body;
    bytes += body.html.length;
  }
  return kept;
}

export class MailThreadCache {
  private entries: MailThreadCacheEntry[];

  constructor(
    private readonly store: SnapshotStore,
    private readonly wsId: string,
    private readonly userId: string,
  ) {
    this.entries = this.readIndex();
  }

  private readIndex(): MailThreadCacheEntry[] {
    const raw = this.store.read(mailThreadIndexKey(this.wsId, this.userId));
    if (raw === undefined) return [];
    try {
      const v = JSON.parse(raw) as unknown;
      return Array.isArray(v) ? (v as MailThreadCacheEntry[]) : [];
    } catch {
      return [];
    }
  }

  private key(threadId: string): string {
    return mailThreadKey(this.wsId, this.userId, threadId);
  }

  /** What's kept of a thread. One it can't read is dropped. */
  read(threadId: string): MailThreadSnapshot | undefined {
    const raw = this.store.read(this.key(threadId));
    if (raw === undefined) return undefined;
    const snapshot = parseMailThreadSnapshot(raw);
    if (snapshot === undefined) this.drop(threadId);
    return snapshot;
  }

  /** Keeps a thread as the most recently opened; older ones go past the limits. */
  write(threadId: string, snapshot: MailThreadSnapshot, now: number): void {
    const entry = { id: threadId, at: now, bytes: threadBytes(snapshot.messages, snapshot.bodies) };
    const { keep, drop } = trimMailThreadCache([...this.entries.filter((e) => e.id !== threadId), entry], MAIL_THREAD_CACHE_MAX, MAIL_THREAD_CACHE_BYTES);
    this.entries = keep;
    for (const id of drop) this.store.remove(this.key(id));
    if (!drop.includes(threadId)) this.store.write(this.key(threadId), () => JSON.stringify(snapshot));
    this.saveIndex();
  }

  /** The thread is gone from the server. */
  drop(threadId: string): void {
    this.store.remove(this.key(threadId));
    this.entries = this.entries.filter((e) => e.id !== threadId);
    this.saveIndex();
  }

  private saveIndex(): void {
    const entries = this.entries;
    this.store.write(mailThreadIndexKey(this.wsId, this.userId), () => JSON.stringify(entries));
  }
}
